import { app } from "@microsoft/teams-js";
import type { ThemeMode } from "@contexts/ThemeContext";
import { createDebugLogger } from "@utils/debugLog";

const log = createDebugLogger("embed:teams");

let initializePromise: Promise<void> | null = null;

/**
 * Initialises the Teams SDK once for this window.
 *
 * Every Teams SDK call other than `app.initialize` itself throws
 * "The library has not yet been initialized" until this has resolved, so both
 * the tab and the sign-in popup have to await it before touching the SDK. A
 * failed attempt is not cached: outside Teams the call never resolves to a
 * host, and a caller that retries should get a fresh attempt rather than a
 * permanently poisoned promise.
 */
export const ensureTeamsSdkReady = async (): Promise<void> => {
  if (!initializePromise) {
    initializePromise = app.initialize().catch((error: unknown) => {
      initializePromise = null;
      throw error;
    });
  }

  await initializePromise;
};

/**
 * Teams reports "default", "dark", "contrast" or "glass". This app has no
 * high-contrast theme, so "contrast" maps to dark — the closer of the two in
 * luminance. Anything unrecognised, "glass" included, falls back to light.
 * "classic" is a OneTaskList theme with no Teams counterpart, so it is never
 * selected here.
 */
export const mapTeamsThemeToThemeMode = (teamsTheme: string): ThemeMode =>
  teamsTheme === "dark" || teamsTheme === "contrast" ? "dark" : "light";

/**
 * Tells Teams the tab finished loading. Teams shows its own loading indicator
 * until this arrives when the manifest opts into one, and times the tab out
 * with "There was a problem reaching this app" if it never does.
 */
export const notifyTeamsTabLoaded = async (): Promise<void> => {
  log("notifying Teams the tab is ready");
  await app.notifySuccess();
};

/**
 * Tells Teams the tab could not start. Teams then shows its own error surface
 * rather than leaving the user on an indefinite loading indicator.
 */
export const notifyTeamsTabFailed = (reason: string): void => {
  log("notifying Teams the tab failed", { reason });
  app.notifyFailure({
    reason: app.FailedReason.Other,
    message: reason,
  });
};

/**
 * Opens a URL through the Teams client instead of from the frame.
 *
 * A tab is an iframe, so an ordinary target="_blank" leaves it to the browser's
 * pop-up handling — the same thing that catches the sign-in window. Handing the
 * URL to the client lets Teams open it the way the user's own settings say to,
 * and keeps the tab where it is.
 */
export const openTeamsLink = async (url: string): Promise<void> => {
  log("opening a link through the Teams client", { url });
  await ensureTeamsSdkReady();
  await app.openLink(url);
};

/** The theme Teams is currently using, or null when the context has none. */
export const getTeamsTheme = async (): Promise<string | null> => {
  const context = await app.getContext();
  return context.app.theme ?? null;
};

/** Calls back whenever the user changes the Teams client theme. */
export const onTeamsThemeChange = (
  handler: (themeMode: ThemeMode) => void,
): void => {
  app.registerOnThemeChangeHandler((teamsTheme: string) => {
    log("Teams theme changed", { teamsTheme });
    handler(mapTeamsThemeToThemeMode(teamsTheme));
  });
};
